import type {
  AuthorizedOrderDetailVo,
  AuthorizedOrderGeneratePurchaseParam,
  AuthorizedOrderItemPurchaseInfoParam,
  AuthorizedOrderLinkPurchaseParam,
  AuthorizedOrderListVo,
  AuthorizedOrderPatchParam,
  AuthorizedOrderQueryParam,
  AuthorizedOrderSyncParam,
  AuthorizedOrderSyncTaskVo,
} from './model';

import { useAccessStore } from '@vben/stores';

import { baseRequestClient, requestClient } from '#/api/request';

export function listAuthorizedOrders(param: AuthorizedOrderQueryParam) {
  return requestClient.post<{ rows: AuthorizedOrderListVo[]; total: number }>(
    '/merchant/authorized-order/list',
    param,
  );
}

/**
 * 各 tab 状态计数：返回 { total, WAIT_SHIP, PART_SHIP, WAIT_PICKUP,
 * IN_TRANSIT, DELIVERED, COMPLETED, CANCELED } map。
 * 后端剔除了 externalStatusMapped / processStatus 维度筛选。
 */
export function statsAuthorizedOrders(
  param: Omit<
    AuthorizedOrderQueryParam,
    'externalStatusMapped' | 'pageNum' | 'pageSize' | 'processStatus'
  >,
) {
  return requestClient.post<Record<string, number>>(
    '/merchant/authorized-order/stats',
    param,
  );
}

export function getAuthorizedOrderDetail(id: number) {
  return requestClient.get<AuthorizedOrderDetailVo>(
    `/merchant/authorized-order/${id}`,
  );
}

/** 删除手工订单（仅 platformCode=MANUAL 且未关联采购） */
export function deleteAuthorizedOrder(id: number) {
  return requestClient.delete(`/merchant/authorized-order/${id}`);
}

export function syncAuthorizedOrders(param: AuthorizedOrderSyncParam) {
  return requestClient.post<AuthorizedOrderSyncTaskVo>(
    '/merchant/authorized-order/sync',
    param,
    { timeout: 600_000 },
  );
}

export function getAuthorizedOrderSyncTask(taskId: number) {
  return requestClient.get<AuthorizedOrderSyncTaskVo>(
    `/merchant/authorized-order/sync-task/${taskId}`,
  );
}

export function retryMissingAuthorizedOrderWaybill(limit?: number) {
  return requestClient.post(
    '/merchant/authorized-order/waybill/retry-missing',
    {
      limit,
    },
  );
}

export function retryAuthorizedOrderWaybill(id: number) {
  return requestClient.post(`/merchant/authorized-order/${id}/waybill/retry`);
}

export function validateAuthorizedOrder(id: number) {
  return requestClient.post(`/merchant/authorized-order/${id}/validate`);
}

export function patchAuthorizedOrder(
  id: number,
  param: AuthorizedOrderPatchParam,
) {
  return requestClient.request(`/merchant/authorized-order/${id}`, {
    data: param,
    method: 'PATCH',
  });
}

export function saveAuthorizedOrderColorTag(
  id: number,
  colorTag?: null | string,
) {
  return requestClient.post(`/merchant/authorized-order/${id}/color-tag`, {
    colorTag,
  });
}

export function batchSaveAuthorizedOrderColorTag(
  ids: number[],
  colorTag?: null | string,
) {
  return requestClient.post('/merchant/authorized-order/color-tag/batch', {
    colorTag,
    ids,
  });
}

export function saveAuthorizedOrderItemPurchaseInfo(
  id: number,
  items: AuthorizedOrderItemPurchaseInfoParam[],
) {
  return requestClient.post(
    `/merchant/authorized-order/${id}/items/purchase-info`,
    {
      items,
    },
  );
}

export function generateAuthorizedOrderPurchase(
  param: AuthorizedOrderGeneratePurchaseParam,
) {
  return requestClient.post(
    '/merchant/authorized-order/generate-purchase',
    param,
  );
}

export function linkAuthorizedOrderPurchase(
  id: number,
  param: AuthorizedOrderLinkPurchaseParam,
) {
  return requestClient.post(
    `/merchant/authorized-order/${id}/link-purchase`,
    param,
  );
}

export function linkAuthorizedOrderInbound(
  id: number,
  inboundOrderIds: number[],
) {
  return requestClient.post(`/merchant/authorized-order/${id}/link-inbound`, {
    inboundOrderIds,
  });
}

export function linkAuthorizedOrderOutbound(
  id: number,
  outboundOrderIds: number[],
) {
  return requestClient.post(`/merchant/authorized-order/${id}/link-outbound`, {
    outboundOrderIds,
  });
}

export function linkAuthorizedOrderPreOnline(
  id: number,
  preOnlineOrderIds: number[],
) {
  return requestClient.post(
    `/merchant/authorized-order/${id}/link-pre-online`,
    { preOnlineOrderIds },
  );
}

export function ignoreAuthorizedOrder(id: number, reason?: string) {
  return requestClient.post(`/merchant/authorized-order/${id}/ignore`, {
    reason,
  });
}

export interface AuthorizedOrderWaybillParam {
  /** 长（英寸） */
  length: string;
  /** 宽（英寸） */
  width: string;
  /** 高（英寸） */
  height: string;
  /** 重量（磅） */
  weight: string;
  /** 物流服务 ID（查可用时为空，购买时必填） */
  shippingServiceId?: string;
}

export interface ShippingServiceOption {
  id: string;
  name: string;
  price?: string;
  currency?: string;
  earliestDeliveryDays?: number;
  latestDeliveryDays?: number;
  shippingProviderId?: string;
  shippingProviderName?: string;
  isDefault?: boolean;
}

/** 查可用物流服务（购买面单对话框初始化时拉一次） */
export function listWaybillShippingServices(
  id: number,
  param: AuthorizedOrderWaybillParam,
) {
  return requestClient.post<ShippingServiceOption[]>(
    `/merchant/authorized-order/${id}/waybill/shipping-services`,
    param,
  );
}

/** 调 TK fulfillment 完整流程购买面单 */
export function buyAuthorizedOrderWaybill(
  id: number,
  param: AuthorizedOrderWaybillParam,
) {
  return requestClient.post(
    `/merchant/authorized-order/${id}/waybill/buy`,
    param,
  );
}

export function uploadManualAuthorizedOrderWaybill(
  id: number,
  waybillNo: string,
  file: File,
) {
  const formData = new FormData();
  formData.append('waybillNo', waybillNo);
  formData.append('file', file);
  return requestClient.post(
    `/merchant/authorized-order/${id}/waybill/manual-upload`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data; boundary=WebAppBoundary',
      },
    },
  );
}

export interface AuthorizedOrderImportRow {
  rowNum: number;
  // A 区·订单
  platformOrderNo: string;
  shopName?: string;
  externalPaidAt?: string;
  externalShipDeadlineAt?: string;
  buyerName?: string;
  buyerPhone?: string;
  buyerEmail?: string;
  recipientName?: string;
  recipientPhone?: string;
  countryCode?: string;
  stateProvince?: string;
  city?: string;
  postalCode?: string;
  addressLine?: string;
  warehouseId?: string;
  waybillNo?: string;
  waybillFileUrl?: string;
  customerNote?: string;
  // B 区·商品
  sku?: string;
  productName?: string;
  spec?: string;
  quantity?: number;
  unitPrice?: string;
  currency?: string;
  imageUrl?: string;
  // C 区·采购（purchaseAsin 后端自动从 sku 同值，不在导入字段里）
  purchaseLink?: string;
  purchaseUserRemark?: string;
}

export interface AuthorizedOrderImportFailRow extends AuthorizedOrderImportRow {
  errors: string[];
}

export interface AuthorizedOrderImportCheckResult {
  totalRows: number;
  successList: AuthorizedOrderImportRow[];
  failList: AuthorizedOrderImportFailRow[];
}

export interface AuthorizedOrderImportDoResult {
  createdCount: number;
  skippedExistsCount: number;
  groupCount: number;
}

/** 第一阶段：解析 Excel + 校验 + 检查重复，不落库 */
export function importAuthorizedOrderCheck(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return requestClient.post<AuthorizedOrderImportCheckResult>(
    '/merchant/authorized-order/import-check',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data; boundary=WebAppBoundary',
      },
    },
  );
}

/** 第二阶段：拿 check 后 successList 真落库 */
export function importAuthorizedOrderDo(
  successList: AuthorizedOrderImportRow[],
) {
  return requestClient.post<AuthorizedOrderImportDoResult>(
    '/merchant/authorized-order/import-do',
    { successList },
  );
}

/** 下载导入模板 xlsx（后端 ClassPathResource 直接吐流）。
 *  用 vben baseRequestClient（带 apiURL baseURL）+ responseType:'blob'，
 *  避免：1) dev / prod baseURL 不一致；2) prod pinia 加密 localStorage。
 *
 *  ⚠️ baseRequestClient 用 `new RequestClient(...)` 直接 new，**没经过 createRequestClient
 *  的 addRequestInterceptor**，所以不会自动注入 token header（requestClient 才有）。
 *  这里手动从 useAccessStore 取 accessToken 加到 headers.token。 */
export async function downloadAuthorizedOrderTemplate() {
  const accessStore = useAccessStore();
  const token = accessStore.accessToken;
  if (!token) {
    throw new Error('登录状态丢失，请重新登录后再下载模板');
  }
  const res: any = await baseRequestClient.request(
    '/merchant/authorized-order/import-template',
    {
      method: 'GET',
      responseType: 'blob',
      responseReturn: 'raw',
      headers: { token },
    } as any,
  );
  const data = res?.data ?? res;
  // 业务异常时后端可能返回 application/json + 错误信息，按 type 判断
  if (data?.type === 'application/json') {
    const text = await data.text();
    const body = JSON.parse(text || '{}');
    throw new Error(body?.msg || '下载模板失败');
  }
  const blob = data instanceof Blob ? data : new Blob([data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = '授权订单导入模板.xlsx';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
